import { DateFormat } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { CreateTreatmentEpisodeModalValues } from '@equip.health/ui/lib/types/src/maud/modals/CreateTreatmentEpisode/CreateTreatmentEpisodeModal';

import {
  PauseDetails,
  RelapsePreventionDetails,
  TreatmentDetails,
  TreatmentEpisodeProgramDetails,
} from '~/components/maud/patients/treatmentEpisode/Types';
import {
  EpisodeTreatmentProgram,
  TreatmentEpisodeStatus,
} from '~/lib/constants/patients.constants';
import {
  formatDateToUTCString,
  toLocalMidnight,
  toUTCMidnight,
} from '~/lib/util/date.util';

type UpdateDate = {
  treatment: TreatmentDetails;
  pauses: PauseDetails[];
  relapsePrevention?: RelapsePreventionDetails;
  statusReasons: EpisodeStatusReason[];
  statuses: TreatmentEpisodeStatusEnum[];
  treatmentPrograms?: TreatmentEpisodeProgramDetails[];
};

export const toLocalTz = (date: Date) =>
  DateTime.fromJSDate(date, { zone: 'local' }).toFormat(
    DateFormat.shortYearMonthDay,
  );

export const getTreatmentEpisodePrograms = (
  admitDate: Date,
  treatmentProgramName: string,
  endDate?: Date,
): TreatmentEpisodeProgramDetails[] => {
  const equipEligiblePrograms = new Set<string>([
    EpisodeTreatmentProgram.TRANSITION,
    EpisodeTreatmentProgram.OPEN_CARE_MODEL,
  ]);
  const endDateToLocalTz = endDate ? toLocalTz(endDate) : undefined;
  const programs: TreatmentEpisodeProgramDetails[] = [
    {
      endDate: endDateToLocalTz,
      startDate: toLocalTz(admitDate),
      treatmentProgramName,
    },
  ];

  if (endDate && equipEligiblePrograms.has(treatmentProgramName)) {
    const equipTreatmentStart = DateTime.fromFormat(
      endDateToLocalTz,
      DateFormat.shortYearMonthDay,
    )
      .plus({ days: 1 })
      .toFormat(DateFormat.shortYearMonthDay);
    programs.push({
      startDate: equipTreatmentStart,
      treatmentProgramName: EpisodeTreatmentProgram.EQUIP,
    });
  }

  return programs;
};

export const createUpdateRequest = ({
  treatment,
  pauses,
  statusReasons,
  statuses,
  relapsePrevention,
  treatmentPrograms,
}: UpdateDate): UpdateTreatmentEpisodeRequest => {
  const {
    statusDescription,
    dischargeDate,
    level,
    dischargeReason,
    program: treatmentProgramName,
  } = treatment;
  const isDischarged = statusDescription === TreatmentEpisodeStatus.DISCHARGED;

  const admitDate = toUTCMidnight(treatment.admitDate);

  let closedDate = null;
  if (statusDescription === TreatmentEpisodeStatus.NEVER_STARTED) {
    closedDate = admitDate;
  } else if (isDischarged) {
    closedDate = toUTCMidnight(dischargeDate);
  }

  const statusCode = statuses.find(
    (x) => x.description === statusDescription,
  )?.code;
  const statusReasonId = dischargeReason
    ? statusReasons.find(
        (x) => x.status === statusCode && x.label === dischargeReason,
      )?.externalId
    : undefined;

  const pausedDetails: UpdatePausedDetailRequest[] = (pauses || []).map(
    (x) => ({
      endDate: toUTCMidnight(x.end),
      externalId: x.id,
      pausedReasonId: statusReasons.find(
        (s) => s.label === x.reason && s.status === 'PAUSED',
      )?.externalId,
      startDate: toUTCMidnight(x.start),
    }),
  );

  const treatmentProgramsRequest: UpdateTreatmentProgramRequest[] =
    relapsePrevention
      ? [
          {
            endDate: formatDateToUTCString(relapsePrevention.end),
            externalId: relapsePrevention.id,
            startDate: formatDateToUTCString(relapsePrevention.start),
            treatmentProgramName: EpisodeTreatmentProgram.RELAPSE_PREVENTION,
          },
          ...(treatmentPrograms || []),
        ]
      : [...(treatmentPrograms || [])];

  return {
    admitDate,
    closedDate,
    level,
    pausedDetails,
    statusCode,
    ...(statusReasonId && { statusReasonId }),
    treatmentEpisodePrograms: treatmentProgramsRequest,
    treatmentProgramName,
  };
};

/**
 * Describe inquiry for treatment episode accordion.
 * @param inquiry
 */
export const getRelatedInquiry = (inquiry?: InquiryDetailResponse): string => {
  if (!inquiry) return '';

  const dateStr = DateTime.fromJSDate(
    toLocalMidnight(new Date(inquiry.inquiryDate)),
  ).toFormat(DateFormat.longDate);

  return `${inquiry.channel.description}, ${inquiry.status?.name} (Created ${dateStr})`;
};

export const toDate = (dt?: string): Date | undefined =>
  dt ? toLocalMidnight(new Date(dt)) : undefined;

export const getNextProgramStartDate = (date: string) => {
  return DateTime.fromISO(date).plus({ days: 1 }).toISODate();
};

export const changeFirstProgramStartDate = (
  treatmentEpisodeProgramsState: UpdateTreatmentEpisodeProgramsState,
  admitDate: Date,
) => {
  treatmentEpisodeProgramsState.updateProgram(0, {
    ...(admitDate && {
      startDate: DateTime.fromJSDate(admitDate).toFormat(
        DateFormat.shortYearMonthDay,
      ),
    }),
  });
};

export const changeLastProgramEndDate = (
  treatmentEpisodeProgramsState: UpdateTreatmentEpisodeProgramsState,
  endDate: Date,
) => {
  const lastIndex = treatmentEpisodeProgramsState.values.length - 1;
  if (lastIndex < 0) return;
  treatmentEpisodeProgramsState.updateProgram(lastIndex, {
    endDate,
  });
};

export const updateAllProgramDatesByDelta = (
  treatmentEpisodeProgramsState: UpdateTreatmentEpisodeProgramsState,
  delta: number,
) => {
  treatmentEpisodeProgramsState.values.forEach((program, index) => {
    const startDate = DateTime.fromFormat(
      program.startDate,
      DateFormat.shortYearMonthDay,
    ).plus({ days: delta });
    const endDate = program.endDate
      ? DateTime.fromFormat(program.endDate, DateFormat.shortYearMonthDay).plus(
          { days: delta },
        )
      : undefined;

    treatmentEpisodeProgramsState.updateProgram(index, {
      ...program,
      startDate: startDate.toFormat(DateFormat.shortYearMonthDay),
      ...(endDate && {
        endDate: endDate.toFormat(DateFormat.shortYearMonthDay),
      }),
    });
  });
};
export const convertToYearMonthDayFormat = (date: string | Date) => {
  if (!date) return undefined;
  if (date instanceof Date) {
    return DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay);
  }
  return DateTime.fromISO(date).toFormat(DateFormat.shortYearMonthDay);
};

export const getProgramsBeforeDischargeDate = (
  dischargeDate: Date,
  programs: TreatmentEpisodeProgramDetails[],
) => {
  return programs.filter((program) => {
    const programStartStr = convertToYearMonthDayFormat(program.startDate);
    const dischargeDateStr = DateTime.fromJSDate(dischargeDate).toISODate();
    return !programStartStr || programStartStr < dischargeDateStr;
  });
};

export const getDischargedPatientUpdatedPrograms = (
  dischargeDate: Date,
  programs: TreatmentEpisodeProgramDetails[],
) => {
  // Always convert dischargeDate to UTC and format as YYYY-MM-DD
  const dischargeDateStr = DateTime.fromJSDate(dischargeDate, {
    zone: 'utc',
  }).toISODate();

  // Filter programs using UTC comparison
  const filteredPrograms = programs.filter((program) => {
    const programStartStr = DateTime.fromISO(program.startDate).toISODate();
    return !programStartStr || programStartStr < dischargeDateStr;
  });

  if (filteredPrograms.length === 0) return [];

  // Update the last program's endDate to the UTC discharge date
  const lastIndex = filteredPrograms.length - 1;
  return filteredPrograms.map((program, idx) => {
    if (idx === lastIndex) {
      return { ...program, endDate: dischargeDateStr };
    }
    return program;
  });
};

/**
 * Formats start and end dates in the desired style.
 * @param {string|null} start - Start date in ISO format (e.g., '2025-08-11') or null.
 * @param {string|null} end - End date in ISO format or null.
 * @returns {string} - Formatted date string.
 */
export const formatProgramDates = (
  start: Date | string,
  end: Date | string,
) => {
  const toDT = (d: Date | string | null | undefined) => {
    if (!d) return undefined;
    if (d instanceof Date) {
      return DateTime.fromJSDate(d);
    }
    return DateTime.fromISO(d);
  };

  const startDT = toDT(start);
  const endDT = toDT(end);
  const now = DateTime.now().startOf('day');

  if (startDT && endDT) {
    if (startDT.year === endDT.year) {
      return `${startDT.toFormat(DateFormat.shortMonthDay)} - ${endDT.toFormat(DateFormat.shortMonthDayYear)}`;
    }
    return `${startDT.toFormat(DateFormat.shortMonthDayYear)} - ${endDT.toFormat(DateFormat.shortMonthDayYear)}`;
  }

  if (startDT) {
    const isFuture = startDT.startOf('day') > now;
    return `${isFuture ? 'Starting' : 'Started'} ${startDT.toFormat(DateFormat.shortMonthDayYear)}`;
  }

  if (endDT) {
    const isFuture = endDT.startOf('day') > now;
    return `${isFuture ? 'Ending' : 'Ended'} ${endDT.toFormat(DateFormat.shortMonthDayYear)}`;
  }

  return 'Not set';
};

export const selectActiveTreatmentEpisodeProgram = (
  programs: TreatmentEpisodeProgramDetails[],
) => {
  const today = DateTime.utc().startOf('day');
  const parsed = programs
    .map((p) => {
      const start = p.startDate
        ? DateTime.fromISO(p.startDate, { zone: 'utc' }).startOf('day')
        : null;
      const end = p.endDate
        ? DateTime.fromISO(p.endDate, { zone: 'utc' }).startOf('day')
        : null;
      return { raw: p, start, end };
    })
    .filter(({ start }) => !!start);

  const active = parsed
    .filter(
      ({ start, end }) => start && start <= today && (!end || end >= today),
    )
    .sort((a, b) => b.start.toMillis() - a.start.toMillis())[0];

  if (active) return active.raw;

  const mostRecent = parsed.toSorted(
    (a, b) => b.start.toMillis() - a.start.toMillis(),
  )[0];
  return mostRecent?.raw ?? programs[0];
};
export const getTreatmentProgramRules = (
  programs?: Array<string | TreatmentEpisodeProgramDetails>,
): Record<string, { requireEndDate?: boolean }> => {
  const programNames =
    programs && programs.length > 0
      ? programs
          .map((p) => (typeof p === 'string' ? p : p.treatmentProgramName))
          .filter((n): n is string => !!n)
      : Object.values(EpisodeTreatmentProgram);

  const rules: Record<string, { requireEndDate?: boolean }> = {};
  programNames.forEach((name) => {
    if (rules[name]) return;
    const requireEnd =
      name === EpisodeTreatmentProgram.OPEN_CARE_MODEL ||
      name === EpisodeTreatmentProgram.TRANSITION;
    rules[name] = requireEnd ? { requireEndDate: true } : {};
  });
  return rules;
};

export const handleAdmitDateOrProgramChange = ({
  admitDate,
  treatmentProgram,
}: CreateTreatmentEpisodeModalValues): Date | undefined => {
  if (admitDate && treatmentProgram === EpisodeTreatmentProgram.TRANSITION) {
    const d = new Date(admitDate);
    d.setDate(d.getDate() + 14);
    return d;
  }
  return undefined;
};
